import { useStoredErrorText } from "@/core/api";
import { tKey, type I18nKey } from "@/core/i18n";
import { Badge } from "@/modules/shadcn/ui/badge";
import { Button } from "@/modules/shadcn/ui/button";
import { Skeleton } from "@/modules/shadcn/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/modules/shadcn/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modules/shadcn/ui/tooltip";
import {
  Model,
  ModelConversion,
  ModelConversionStatusEnum,
  ModelOutputTypeEnum,
  ModelStatusEnum,
  Rvc4QuantizationModeEnum,
  type ConversionTarget,
} from "@repo/schema";
import type { TFunction } from "i18next";
import {
  Check,
  Download,
  Plus,
  RotateCw,
  ScrollText,
  Square,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useActivateModelConversionMutation,
  useCancelModelConversionMutation,
  useCreateModelConversionMutation,
  useDeleteModelConversionMutation,
  useGetModelConversionsQuery,
  useGetModelOutputsQuery,
  useRetryModelConversionMutation,
} from "../../services";
import { AddConversionDialog } from "./AddConversionDialog";
import { ConversionLogDialog } from "./ConversionLogDialog";

export interface ModelOutputsProps {
  model: Model;
  projectId: number;
}

const STATUS_VARIANT: Record<
  ModelConversionStatusEnum,
  "default" | "secondary" | "destructive" | "outline"
> = {
  [ModelConversionStatusEnum.PENDING]: "secondary",
  [ModelConversionStatusEnum.RUNNING]: "secondary",
  [ModelConversionStatusEnum.DONE]: "default",
  [ModelConversionStatusEnum.FAILED]: "destructive",
  [ModelConversionStatusEnum.CANCELLED]: "outline",
};

const STATUS_LABELS = {
  [ModelConversionStatusEnum.PENDING]: tKey("model:conversionStatus.PENDING"),
  [ModelConversionStatusEnum.RUNNING]: tKey("model:conversionStatus.RUNNING"),
  [ModelConversionStatusEnum.DONE]: tKey("model:conversionStatus.DONE"),
  [ModelConversionStatusEnum.FAILED]: tKey("model:conversionStatus.FAILED"),
  [ModelConversionStatusEnum.CANCELLED]: tKey("model:conversionStatus.CANCELLED"),
} satisfies Record<ModelConversionStatusEnum, I18nKey<"model">>;

// "INT8 · int16 mixed" reads better than "INT8 · INT8_INT16_MIXED": the
// redundant precision prefix is dropped.
const MODE_SHORT_LABELS = {
  [Rvc4QuantizationModeEnum.FP16_STANDARD]: tKey("model:outputs.modeShort.FP16_STANDARD"),
  [Rvc4QuantizationModeEnum.INT8_STANDARD]: tKey("model:outputs.modeShort.INT8_STANDARD"),
  [Rvc4QuantizationModeEnum.INT8_ACCURACY_FOCUSED]: tKey(
    "model:outputs.modeShort.INT8_ACCURACY_FOCUSED",
  ),
  [Rvc4QuantizationModeEnum.INT8_INT16_MIXED]: tKey(
    "model:outputs.modeShort.INT8_INT16_MIXED",
  ),
  [Rvc4QuantizationModeEnum.INT8_INT16_MIXED_ACCURACY_FOCUSED]: tKey(
    "model:outputs.modeShort.INT8_INT16_MIXED_ACCURACY_FOCUSED",
  ),
} satisfies Record<Rvc4QuantizationModeEnum, I18nKey<"model">>;

const isInFlight = (status: ModelConversionStatusEnum) =>
  status === ModelConversionStatusEnum.PENDING ||
  status === ModelConversionStatusEnum.RUNNING;

const describeSettings = (
  conversion: ModelConversion,
  t: TFunction<["model", "common"]>,
): string => {
  const parts: string[] = [conversion.precision];
  const mode = conversion.settings.quantizationMode;
  if (mode) parts.push(t(MODE_SHORT_LABELS[mode]));
  return parts.join(" · ");
};

export const ModelOutputs = ({ model, projectId }: ModelOutputsProps) => {
  const { t } = useTranslation(["model", "common"]);
  const [showAdd, setShowAdd] = useState(false);
  const [logConversionId, setLogConversionId] = useState<number | null>(null);
  const storedErrorText = useStoredErrorText();

  const { data: conversions, refetch: refetchConversions, isLoading } =
    useGetModelConversionsQuery({ projectId, modelId: model.id });
  const { data: outputs } = useGetModelOutputsQuery({
    projectId,
    modelId: model.id,
  });

  const [createConversion, { isLoading: isCreating }] =
    useCreateModelConversionMutation();
  const [retryConversion] = useRetryModelConversionMutation();
  const [cancelConversion] = useCancelModelConversionMutation();
  const [activateConversion] = useActivateModelConversionMutation();
  const [deleteConversion] = useDeleteModelConversionMutation();

  const raw = outputs?.find((o) => o.type === ModelOutputTypeEnum.RAW);
  // The archive config is what a conversion rebuilds the NN archive from.
  // Models trained before conversions shipped don't have it, and the head
  // metadata can't be recovered from the ONNX alone — they can only be
  // retrained.
  const isConvertible =
    model.status === ModelStatusEnum.DONE && Boolean(raw?.hasArchiveConfig);

  const anyInFlight = (conversions ?? []).some((c) => isInFlight(c.status));

  // Resolved against the live list rather than snapshotted on click, so an open
  // dialog picks up the status flipping to terminal and the attemptCount bump
  // from a retry — and closes by itself if the conversion is deleted.
  const logConversion =
    (conversions ?? []).find((c) => c.id === logConversionId) ?? null;

  // Polling this endpoint is also what drives reconciliation server-side, so a
  // conversion whose job died silently resolves while anyone is watching.
  useEffect(() => {
    if (!anyInFlight) return;
    const interval = setInterval(() => refetchConversions(), 3000);
    return () => clearInterval(interval);
  }, [anyInFlight, refetchConversions]);

  const run = async (action: Promise<unknown>, failure: string) => {
    try {
      await action;
    } catch {
      toast.error(failure);
    }
  };

  const ids = { projectId, modelId: model.id };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold">{t("outputs.title")}</h2>
          <span className="text-muted-foreground text-sm">
            {t("outputs.description")}
          </span>
        </div>
        <Tooltip>
          {/* Wrapped in a span so the tooltip still fires while the button is
              disabled — a disabled button emits no pointer events. */}
          <TooltipTrigger render={<span />}>
            <Button
              size="sm"
              disabled={!isConvertible}
              onClick={() => setShowAdd(true)}
            >
              <Plus className="mr-1 size-4" />
              {t("outputs.addConversion")}
            </Button>
          </TooltipTrigger>
          {!isConvertible && (
            <TooltipContent>
              {model.status === ModelStatusEnum.DONE
                ? t("outputs.notConvertibleLegacy")
                : t("outputs.notConvertibleYet")}
            </TooltipContent>
          )}
        </Tooltip>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("outputs.table.target")}</TableHead>
              <TableHead>{t("outputs.table.settings")}</TableHead>
              <TableHead>{t("outputs.table.status")}</TableHead>
              <TableHead className="text-right">
                {t("outputs.table.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {raw && (
              <TableRow>
                <TableCell className="font-bold">RAW</TableCell>
                <TableCell className="text-muted-foreground">
                  {t("outputs.onnxExport")}
                </TableCell>
                <TableCell>
                  <Badge variant="default">{t("outputs.ready")}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2"
                    render={<a href={raw.filePath} download />}
                  >
                    <Download className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {(conversions ?? []).map((conversion) => {
              const active = conversion.output?.active ?? false;
              const failure = storedErrorText(conversion);
              return (
                <TableRow key={conversion.id}>
                  <TableCell className="font-bold">
                    <span className="flex items-center gap-2">
                      {conversion.target}
                      {active && (
                        <Tooltip>
                          <TooltipTrigger render={<span />}>
                            <Badge variant="outline">
                              {t("outputs.deployed")}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t("outputs.deployedTooltip")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {describeSettings(conversion, t)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-2">
                        <Badge variant={STATUS_VARIANT[conversion.status]}>
                          {t(STATUS_LABELS[conversion.status])}
                        </Badge>
                        {conversion.attemptCount > 1 && (
                          <span className="text-muted-foreground text-xs">
                            {t("outputs.attempt", {
                              attempt: conversion.attemptCount,
                            })}
                          </span>
                        )}
                      </span>
                      {failure && (
                        <span className="text-destructive text-xs break-words">
                          {failure.headline}
                        </span>
                      )}
                      {failure?.details && (
                        <span className="text-muted-foreground text-xs break-words">
                          {failure.details}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {conversion.hasLog && (
                        <Tooltip>
                          <TooltipTrigger render={<span />}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-2"
                              onClick={() => setLogConversionId(conversion.id)}
                            >
                              <ScrollText className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("outputs.viewLog")}</TooltipContent>
                        </Tooltip>
                      )}
                      {conversion.output && !active && (
                        <Tooltip>
                          <TooltipTrigger render={<span />}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-2"
                              onClick={() =>
                                run(
                                  activateConversion({
                                    ...ids,
                                    conversionId: conversion.id,
                                  }).unwrap(),
                                  t("outputs.errors.activate"),
                                )
                              }
                            >
                              <Check className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("outputs.deployThis")}</TooltipContent>
                        </Tooltip>
                      )}
                      {conversion.output && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2"
                          render={<a href={conversion.output.filePath} download />}
                        >
                          <Download className="size-4" />
                        </Button>
                      )}
                      {isInFlight(conversion.status) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2"
                          onClick={() =>
                            run(
                              cancelConversion({
                                ...ids,
                                conversionId: conversion.id,
                              }).unwrap(),
                              t("outputs.errors.cancel"),
                            )
                          }
                        >
                          <Square className="size-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2"
                          disabled={!isConvertible}
                          onClick={() =>
                            run(
                              retryConversion({
                                ...ids,
                                conversionId: conversion.id,
                              }).unwrap(),
                              t("outputs.errors.retry"),
                            )
                          }
                        >
                          <RotateCw className="size-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        onClick={() =>
                          run(
                            deleteConversion({
                              ...ids,
                              conversionId: conversion.id,
                            }).unwrap(),
                            t("outputs.errors.delete"),
                          )
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}

            {!raw && (conversions ?? []).length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-muted-foreground py-8 text-center"
                >
                  {t("outputs.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {logConversion && (
        <ConversionLogDialog
          projectId={projectId}
          modelId={model.id}
          conversion={logConversion}
          onClose={() => setLogConversionId(null)}
        />
      )}

      {showAdd && (
        <AddConversionDialog
          backend={model.backend}
          trainingType={model.trainingType}
          canQuantize={model.hasCalibration}
          isLoading={isCreating}
          onCancel={() => setShowAdd(false)}
          onConfirm={async ({ target, precision, quantizationMode }) => {
            try {
              await createConversion({
                ...ids,
                target: target as ConversionTarget,
                precision,
                settings: {
                  quantizationMode,
                  calibrationMaxImages: 400,
                  overrides: {},
                },
              }).unwrap();
              setShowAdd(false);
            } catch {
              toast.error(t("outputs.errors.start"));
            }
          }}
        />
      )}
    </div>
  );
};
